import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  CalendarCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Car,
  User,
  Calendar,
  DollarSign,
  ArrowRight,
  CheckCircle,
  XCircle,
  FileText
} from 'lucide-react';

const maskPhone = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{1})(\d{4})(\d{4})$/, '$1 $2-$3');
};

const maskCPF = (value) => {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const statusColors = {
  ativa: 'bg-green-500',
  concluida: 'bg-blue-500',
  cancelada: 'bg-red-500'
};

const statusLabels = {
  ativa: 'Ativa',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

export default function ReservationsPage() {
  const { API } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ativa'); // Filtro padrão: ATIVAS
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Form state
  const [formData, setFormData] = useState({
    cliente_nome: '',
    cliente_telefone: '',
    cliente_cpf: '',
    vehicle_id: '',
    data_reserva: new Date().toISOString().split('T')[0],
    prazo_entrega_dias: 30,
    valor_pago: 0,
    observacoes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReservations();
    loadVehicles();
  }, [statusFilter]);

  const loadReservations = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'todas') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/reservations?${params}`, { withCredentials: true });
      setReservations(response.data);
    } catch (error) {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?status=disponivel`, { withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (formMode === 'create') {
        await axios.post(`${API}/reservations`, formData, { withCredentials: true });
        toast.success('Reserva criada com sucesso!');
      } else {
        await axios.put(`${API}/reservations/${selectedReservation.reservation_id}`, formData, { withCredentials: true });
        toast.success('Reserva atualizada com sucesso!');
      }
      setShowModal(false);
      resetForm();
      loadReservations();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar reserva');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/reservations/${selectedReservation.reservation_id}`, { withCredentials: true });
      toast.success('Reserva excluída com sucesso!');
      setShowDeleteDialog(false);
      loadReservations();
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao excluir reserva');
    }
  };

  const handleStatusChange = async (reservation, newStatus) => {
    try {
      await axios.put(`${API}/reservations/${reservation.reservation_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Reserva ${newStatus === 'concluida' ? 'concluída' : 'cancelada'} com sucesso!`);
      loadReservations();
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleVehicleLink = async (vehicleId) => {
    try {
      await axios.put(`${API}/reservations/${selectedReservation.reservation_id}`, { vehicle_id: vehicleId }, { withCredentials: true });
      toast.success('Veículo vinculado com sucesso!');
      setShowVehicleModal(false);
      loadReservations();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular veículo');
    }
  };

  const handleGenerateDocument = async (reservationId) => {
    try {
      toast.loading('Gerando termo de reserva...');
      const response = await axios.get(
        `${API}/reservations/${reservationId}/generate-document`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `termo_reserva_${reservationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Termo gerado com sucesso!');
    } catch (error) {
      toast.dismiss();
      toast.error('Erro ao gerar termo de reserva');
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_nome: '',
      cliente_telefone: '',
      cliente_cpf: '',
      vehicle_id: '',
      data_reserva: new Date().toISOString().split('T')[0],
      prazo_entrega_dias: 30,
      valor_pago: 0,
      observacoes: ''
    });
  };

  const openCreateModal = () => {
    setFormMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (reservation) => {
    setFormMode('edit');
    setSelectedReservation(reservation);
    setFormData({
      cliente_nome: reservation.cliente_nome,
      cliente_telefone: reservation.cliente_telefone,
      cliente_cpf: reservation.cliente_cpf || '',
      vehicle_id: reservation.vehicle_id || '',
      data_reserva: reservation.data_reserva,
      prazo_entrega_dias: reservation.prazo_entrega_dias,
      valor_pago: reservation.valor_pago,
      observacoes: reservation.observacoes || ''
    });
    setShowModal(true);
  };

  const openViewModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowViewModal(true);
  };

  const openDeleteDialog = (reservation) => {
    setSelectedReservation(reservation);
    setShowDeleteDialog(true);
  };

  const openVehicleModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowVehicleModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6" data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Reservas</h1>
          <p className="text-gray-500 text-sm">Gerencie as reservas de veículos</p>
        </div>
        <Button onClick={openCreateModal} data-testid="add-reservation-button">
          <Plus className="w-4 h-4 mr-2" />
          Nova Reserva
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="ativa">Ativas</SelectItem>
                <SelectItem value="concluida">Concluídas</SelectItem>
                <SelectItem value="cancelada">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : reservations.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <CalendarCheck className="w-12 h-12 mb-2 opacity-50" />
          <p>Nenhuma reserva encontrada</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reservations.map((reservation) => (
            <Card 
              key={reservation.reservation_id} 
              className="hover:shadow-lg transition-shadow duration-200 border-l-4"
              style={{ borderLeftColor: reservation.status === 'ativa' ? '#22c55e' : reservation.status === 'concluida' ? '#3b82f6' : '#ef4444' }}
              data-testid={`reservation-card-${reservation.reservation_id}`}
            >
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">{reservation.cliente_nome}</h3>
                    <p className="text-sm text-gray-500">{reservation.cliente_telefone}</p>
                  </div>
                  <Badge className={`${statusColors[reservation.status]} text-white`}>
                    {statusLabels[reservation.status]}
                  </Badge>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500 text-xs">Data Reserva</p>
                    <p className="font-medium">{formatDate(reservation.data_reserva)}</p>
                  </div>
                  <div className="bg-blue-50 p-2 rounded">
                    <p className="text-blue-600 text-xs">Previsão Entrega</p>
                    <p className="font-medium text-blue-700">{formatDate(reservation.previsao_entrega)}</p>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <p className="text-green-600 text-xs">Valor Pago</p>
                    <p className="font-semibold text-green-700">{formatCurrency(reservation.valor_pago)}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500 text-xs">Veículo</p>
                    {reservation.vehicle_id ? (
                      <p className="font-medium">{reservation.vehicle_id}</p>
                    ) : (
                      <p className="text-gray-400 italic">Não vinculado</p>
                    )}
                  </div>
                </div>

                {/* Prazo */}
                <div className="text-xs text-gray-500 mb-3">
                  Prazo: {reservation.prazo_entrega_dias} dias
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openViewModal(reservation)}
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {reservation.status === 'ativa' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(reservation)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateDocument(reservation.reservation_id)}
                          title="Gerar Termo de Reserva"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Termo
                        </Button>
                        {!reservation.vehicle_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openVehicleModal(reservation)}
                            title="Vincular veículo"
                          >
                            <Car className="w-4 h-4 mr-1" />
                            Vincular
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  
                  {reservation.status === 'ativa' && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusChange(reservation, 'concluida')}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Concluir reserva"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusChange(reservation, 'cancelada')}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        title="Cancelar reserva"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(reservation)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Excluir reserva"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Nova Reserva' : 'Editar Reserva'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="cliente_nome">Nome do Cliente *</Label>
                <Input
                  id="cliente_nome"
                  value={formData.cliente_nome}
                  onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                  required
                  data-testid="reservation-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cliente_telefone">Telefone *</Label>
                <Input
                  id="cliente_telefone"
                  value={formData.cliente_telefone}
                  onChange={(e) => setFormData({ ...formData, cliente_telefone: maskPhone(e.target.value) })}
                  placeholder="(00) 0 0000-0000"
                  required
                  data-testid="reservation-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cliente_cpf">CPF (opcional)</Label>
                <Input
                  id="cliente_cpf"
                  value={formData.cliente_cpf}
                  onChange={(e) => setFormData({ ...formData, cliente_cpf: maskCPF(e.target.value) })}
                  placeholder="000.000.000-00"
                  data-testid="reservation-cpf-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_reserva">Data da Reserva *</Label>
                  <Input
                    id="data_reserva"
                    type="date"
                    value={formData.data_reserva}
                    onChange={(e) => setFormData({ ...formData, data_reserva: e.target.value })}
                    required
                    data-testid="reservation-date-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="prazo_entrega_dias">Prazo de Entrega (dias) *</Label>
                  <Input
                    id="prazo_entrega_dias"
                    type="number"
                    min="1"
                    value={formData.prazo_entrega_dias}
                    onChange={(e) => setFormData({ ...formData, prazo_entrega_dias: parseInt(e.target.value) || 1 })}
                    required
                    data-testid="reservation-prazo-input"
                  />
                </div>
              </div>

              {/* Data Prevista - Calculada automaticamente */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <Label className="text-blue-700 text-sm font-medium">📅 Data Prevista de Entrega</Label>
                <p className="text-lg font-bold text-blue-800 mt-1">
                  {formData.data_reserva && formData.prazo_entrega_dias ? 
                    (() => {
                      const dataReserva = new Date(formData.data_reserva + 'T00:00:00');
                      const dataPrevisao = new Date(dataReserva);
                      dataPrevisao.setDate(dataPrevisao.getDate() + formData.prazo_entrega_dias);
                      return dataPrevisao.toLocaleDateString('pt-BR');
                    })()
                    : '-'
                  }
                </p>
              </div>

              <div>
                <Label htmlFor="valor_pago">Valor Pago (R$) *</Label>
                <Input
                  id="valor_pago"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor_pago}
                  onChange={(e) => setFormData({ ...formData, valor_pago: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="reservation-valor-input"
                />
              </div>

              {formMode === 'create' && vehicles.length > 0 && (
                <div>
                  <Label htmlFor="vehicle_id">Veículo (opcional)</Label>
                  <Select 
                    value={formData.vehicle_id || "sem_veiculo"} 
                    onValueChange={(value) => setFormData({ ...formData, vehicle_id: value === "sem_veiculo" ? '' : value })}
                  >
                    <SelectTrigger data-testid="reservation-vehicle-select">
                      <SelectValue placeholder="Selecione um veículo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sem_veiculo">Sem veículo</SelectItem>
                      {vehicles.map((vehicle) => (
                        <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {vehicle.vehicle_id} - {vehicle.marca} {vehicle.modelo} - {vehicle.placa}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  rows={3}
                  data-testid="reservation-obs-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-reservation-button">
                {submitting ? 'Salvando...' : formMode === 'create' ? 'Criar Reserva' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Reserva</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <h3 className="font-semibold text-lg">{selectedReservation.cliente_nome}</h3>
                  <p className="text-sm text-gray-500">ID: {selectedReservation.reservation_id}</p>
                </div>
                <Badge className={`${statusColors[selectedReservation.status]} text-white`}>
                  {statusLabels[selectedReservation.status]}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Telefone</p>
                  <p className="font-medium">{selectedReservation.cliente_telefone}</p>
                </div>
                {selectedReservation.cliente_cpf && (
                  <div>
                    <p className="text-gray-500">CPF</p>
                    <p className="font-medium">{selectedReservation.cliente_cpf}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Data da Reserva</p>
                  <p className="font-medium">{formatDate(selectedReservation.data_reserva)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Previsão de Entrega</p>
                  <p className="font-medium">{formatDate(selectedReservation.previsao_entrega)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Prazo</p>
                  <p className="font-medium">{selectedReservation.prazo_entrega_dias} dias</p>
                </div>
                <div>
                  <p className="text-gray-500">Valor Pago</p>
                  <p className="font-semibold text-green-600">{formatCurrency(selectedReservation.valor_pago)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Veículo</p>
                  <p className="font-medium">
                    {selectedReservation.vehicle_id || 'Não vinculado'}
                  </p>
                </div>
              </div>

              {selectedReservation.observacoes && (
                <div>
                  <p className="text-gray-500 text-sm">Observações</p>
                  <p className="text-sm">{selectedReservation.observacoes}</p>
                </div>
              )}
              
              {selectedReservation.status === 'ativa' && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => handleStatusChange(selectedReservation, 'cancelada')}
                    className="text-red-600"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={() => handleStatusChange(selectedReservation, 'concluida')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Concluir
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle Link Modal */}
      <Dialog open={showVehicleModal} onOpenChange={setShowVehicleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Vincular Veículo à Reserva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Selecione um veículo disponível para vincular à reserva de {selectedReservation?.cliente_nome}
            </p>
            
            {vehicles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum veículo disponível</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {vehicles.map((vehicle) => (
                  <div
                    key={vehicle.vehicle_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleVehicleLink(vehicle.vehicle_id)}
                  >
                    <div>
                      <p className="font-medium">{vehicle.vehicle_id} - {vehicle.marca} {vehicle.modelo}</p>
                      <p className="text-sm text-gray-500">{vehicle.placa} - {vehicle.ano_modelo}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a reserva de "{selectedReservation?.cliente_nome}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-reservation"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
